import { useApiClient } from '@m43/core';
import { EventPanelResource, EventPanelView } from '@mark43/cad-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useCurrentUser } from '../current-user/current-user';

export function useEventPanelUpdates() {
    const currentUser = useCurrentUser();
    const queryClient = useQueryClient();
    const { eventPanelUpdatesBundle } = useBootstrapState();
    const { rollOutFEIdAwareStreamsEnabled } = useEnabledFeatures();
    const initialEventPanelViews = eventPanelUpdatesBundle?.eventPanelViews;

    const cacheEventPanelView = useCallback(
        (eventPanelView: EventPanelView) => {
            queryClient.setQueryDefaults(['event-panel-view', eventPanelView.form.eventId], {
                cacheTime: Infinity,
            });
            queryClient.setQueryData(
                ['event-panel-view', eventPanelView.form.eventId],
                eventPanelView
            );
        },
        [queryClient]
    );
    useEffect(() => {
        if (initialEventPanelViews) {
            initialEventPanelViews.forEach((eventPanelView) => {
                cacheEventPanelView(eventPanelView);
            });
        }
    }, [cacheEventPanelView, initialEventPanelViews]);

    const apiClient = useApiClient();
    const eventPanelUpdatesBootstrapQuery = useQuery({
        queryKey: ['unit-assignment-bootstrap'],
        queryFn() {
            return apiClient.request<EventPanelResource.EventPanelUpdatesBootstrap>({
                method: 'POST',
                url: 'event_panel/updates/bootstrap/',
            });
        },
        enabled: false,
    });
    useIntegratedStreams({
        streamNames: [
            rollOutFEIdAwareStreamsEnabled
                ? (`eventPanelUpdates|${currentUser.userId}` as 'eventPanelUpdates')
                : 'eventPanelUpdates',
        ],
        onMessage(message: { payload: { eventPanelViews: EventPanelView[] } }) {
            return message.payload.eventPanelViews;
        },
        promiseBufferCallback: (inputs: EventPanelView[]) => {
            inputs.forEach((eventPanelView) => {
                cacheEventPanelView(eventPanelView);
            });
            return Promise.resolve();
        },
        query: eventPanelUpdatesBootstrapQuery,
    });
}
